import type { HeadingTypes } from '@etg/wings';
import { Card } from '@etg/wings';
import { Accordion, ConditionalWrapper } from '@eti/components';
import { css } from '@eti/styles';
import { useMemo } from 'react';
import { TripType } from '@eti/schema-types';
import BoundTitle from '../common/BoundTitle';
import type { OriginalVersionType } from '../../../../models';
import type { Bound } from '../../../../../../widgets/trip-itinerary/models';
import { transformSegments } from './utils/utils';
import ItineraryBoundHeader from './ItineraryBoundHeader';
import ItineraryEventSegment from './ItineraryEventSegment';
import ItineraryTripSegment from './ItineraryTripSegment';

const wrapperStyles = css`
  padding-top: 8px;
`;

interface ItineraryBoundProps {
  boundId: string;
  boundIndex: number;
  boundHeadingAs?: `h${HeadingTypes['level']}`;
  isSelected: boolean;
  journeyType: TripType;
  originalVersion?: OriginalVersionType | null;
  segments: Bound['segments'];
}

const ItineraryBound = ({
  boundId,
  boundIndex,
  boundHeadingAs,
  isSelected,
  journeyType,
  originalVersion,
  segments,
}: ItineraryBoundProps) => {
  const transformedSegments = useMemo(() => transformSegments(segments), [segments]);
  const heading = (
    <BoundTitle
      boundId={boundId}
      boundIndex={boundIndex}
      dataTestIdPrefix="itinerary"
      headingAs={boundHeadingAs}
      journeyType={journeyType}
    />
  );

  return (
    <Card>
      <ConditionalWrapper
        condition={journeyType !== TripType.OneWay}
        wrapper={(children) => (
          <Accordion
            id={`itinerary-bound-${boundId}-accordion`}
            isInitiallyExpanded={isSelected}
            variant="full-width"
          >
            <Accordion.Header data-testid={`itinerary-bound-${boundId}-expansion-panel`}>
              {heading}
            </Accordion.Header>
            <Accordion.Body>{children}</Accordion.Body>
          </Accordion>
        )}
      >
        <>
          {journeyType === TripType.OneWay && heading}
          <div className={wrapperStyles} data-testid={`itinerary-bound-${boundId}`}>
            <ItineraryBoundHeader
              dataTestIdPrefix={`itinerary-bound-${boundId}`}
              originalDepartureDate={
                originalVersion ? originalVersion.originalSegments[0].departuredAt : null
              }
              segments={transformedSegments}
            />
            {transformedSegments.map((segment, index) => {
              // TODO: MYP-1758 - Match originalSegment to latestSegment by segmentId
              const originalSegmentIndex = index / 2;
              switch (segment?.__typename) {
                case 'TripSegment':
                  return (
                    <ItineraryTripSegment
                      dataTestIdPrefix={`itinerary-bound-${boundId}-trip-segment-${segment.segmentId}`}
                      isLastSegment={index === segments.length - 1}
                      key={segment.segmentId}
                      originalSegment={
                        originalVersion
                          ? originalVersion.originalSegments[originalSegmentIndex]
                          : null
                      }
                      segment={segment}
                    />
                  );
                case 'EventSegment':
                  return (
                    <ItineraryEventSegment
                      dataTestId={`itinerary-bound-${boundId}-event-segment-${segment.segmentId}`}
                      key={segment.segmentId}
                      segment={segment}
                    />
                  );
                default:
                  return null;
              }
            })}
          </div>
        </>
      </ConditionalWrapper>
    </Card>
  );
};

export default ItineraryBound;
